import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { CONTACT } from "@/data/content";

const LINKS = [
  { to: "/", label: "Accueil", id: "accueil" },
  { to: "/services", label: "Services", id: "services" },
  { to: "/realisations", label: "Réalisations", id: "realisations" },
  { to: "/contact", label: "Contact", id: "contact" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-slate-200 shadow-sm" : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-20 items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group" data-testid="nav-brand-logo">
            <img
              src="/assets/logo-eg.png"
              alt="EG 6.1 — Électricité Générale"
              className="h-12 w-12 rounded-xl object-cover border border-slate-200 shadow-sm transition-transform duration-300 group-hover:scale-105"
            />
            <span className="leading-none">
              <span className="block font-display text-xl font-extrabold tracking-tight text-ink">
                EG <span className="text-brand-blue">6.1</span>
              </span>
              <span className="block text-[10px] font-mono uppercase tracking-[0.28em] text-slate-500">
                Électricité Générale
              </span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1" data-testid="nav-links-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.id}
                to={l.to}
                data-testid={`nav-link-${l.id}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm font-medium tracking-wide transition-colors duration-300 ${
                    isActive ? "text-brand-blue" : "text-slate-600 hover:text-ink"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {l.label}
                    {isActive && (
                      <motion.span
                        layoutId="nav-underline"
                        className="absolute inset-x-4 -bottom-0.5 h-px bg-brand-blue shadow-[0_0_12px_rgba(15,52,140,0.5)]"
                      />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={CONTACT.phoneHref}
              data-testid="cta-phone-call"
              className="hidden sm:inline-flex items-center gap-2 rounded-full bg-brand-blue px-5 py-2.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(15,52,140,0.3)] transition-all duration-300 hover:bg-[#0a2668] hover:-translate-y-0.5"
            >
              <Phone className="h-4 w-4" />
              {CONTACT.phone}
            </a>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="md:hidden flex h-11 w-11 items-center justify-center rounded-xl border border-slate-300 text-ink"
              aria-label="Menu"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="md:hidden overflow-hidden bg-white/95 backdrop-blur-xl border-b border-slate-200"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.id}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.06 * i }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={`nav-mobile-link-${l.id}`}
                    className={({ isActive }) =>
                      `block py-3 font-display text-2xl font-bold ${
                        isActive ? "text-brand-blue" : "text-ink"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <a
                href={CONTACT.phoneHref}
                data-testid="cta-phone-call-mobile"
                className="mt-4 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue px-5 py-3.5 text-base font-semibold text-white"
              >
                <Phone className="h-4 w-4" />
                {CONTACT.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Navbar;
